//core
import React, { useCallback, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDebounce } from "usehooks-ts";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import {
  Box,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Button from "../../../../../UI/Buttons/Button";

//styles
import { useStyles } from "./styles";
import { useHeaderStyles } from "../../../../../UI/PageHeaderStyles/styles";

//hooks
import useDidMountEffect from "../../../../../../customHooks/useDidMountEffect";
import { useUI } from "../../../../../../store/common/ui/useUI";
import { useWebhooksReport } from "../../../../../../store/admin/reports/webhooks_report/useWebhooksReport";
import { usePermissions } from "../../../../../../store/common/configuration/usePermissions";

//selectors
import {
  selectDownloadData,
  selectListData,
  selectOptionsData,
  selectColumnsData
} from "../../../../../../store/admin/reports/webhooks_report/selectors";

//context
import { GlobalContext } from "../../../../../../context/GlobalContext";

//utils
import { getDataIds, getOptionsList, getPeriod } from "../../../utils";

//components
import CategorySearch from "../../../components/CategorySearch";
import DownloadReport from "../DownloadReport";
import TableData from "../../../../../common/TableData";
import MuiModal from "../../../../../UI/MuiModal";
import TableActions from "../TableActions";
import DrawerFilter from "../DrawerFilter";
import { OrderType, IFilterData } from "../../types";
import { TableColumnsItem } from "../../../../../common/FiltersComponents/TableSettings/types";

export type ReportFilterDataType = {
  encoding: string;
  per_page: number;
  filters: {
    sent_from: string;
    sent_to: string;
    affiliateIds: any,
    trackerIds: any,
    statuses: any,
    webhookTypes: any,
    webhookIds: any,
  };
};

export const initialOrderState: OrderType = {
  created_at: "desc",
}

export const initialFilterData: IFilterData = {
  period: 'month',
  sent_from: getPeriod('month').start,
  sent_to: getPeriod('month').end,
  affiliateIds: [],
  trackerIds: [],
  webhookIds: [],
  webhookTypes: [],
  statuses: [],
};

const DesktopReport = () => {
  const styles = useStyles();
  const headerStyles = useHeaderStyles();
  const { hasPermissions } = usePermissions();
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width:767px)');

  const context = useContext(GlobalContext);

  const {
    getList,
    setDownload,
    clearDownloadState,
    resetState,
    getColumns
  } = useWebhooksReport();
  const { setGeneralSnackbar } = useUI();

  const data = useSelector(selectListData);
  const filterData = useSelector(selectOptionsData);
  const downloadData = useSelector(selectDownloadData);
  const columnsData = useSelector(selectColumnsData);

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [showMobileTooltip, setShowMobileTooltip] = useState<boolean>(false);
  const [generateReport, setGenerateReport] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const debouncedValue = useDebounce<string>(searchValue, 500);

  const [drawerFilter, setDrawerFilter] = useState<IFilterData>(initialFilterData);
  const [selectedFilterId, setSelectedFilterId] = useState<string>('');
  const [order, setOrder] = useState<OrderType>(initialOrderState);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [columns, setColumns] = useState<TableColumnsItem[]>([]);
  const [defaultColumns, setDefaultColumns] = useState<TableColumnsItem[]>([]);

  useEffect(() => {
    //get columns data
    if (hasPermissions(["api2.admin.webhookscontroller.sort_columns"])) {
      if (!columns.length) {
        getColumns();
      }
    }
  }, []);

  useEffect(() => {
    if (!!columnsData) {
      const newColumns = columnsData.map((item: string) => {
        return {
          id: item,
          value: item,
          checked: true,
        };
      });

      setColumns(newColumns);
      setDefaultColumns(newColumns);
    }
  }, [columnsData]);

  useEffect(() => {
    if (!!data && !!data.search_fields && !!data.search_fields.length) {
      if (!data.search_fields.includes(searchFilter)) {
        setSearchFilter(getOptionsList(data.search_fields)[0].id);
      }
    }
  }, [data]);

  useDidMountEffect(() => {
    if (!!drawerFilter) {
      const payload = getPayloadData(page+1);
      getList(payload);
    }
  }, [page]);

  useDidMountEffect(() => {
    setPage(0);
    if (!!drawerFilter) {
      const payload = getPayloadData(1);
      getList(payload);
    }
  }, [rowsPerPage, order]);

  useDidMountEffect(() => {
    if (!!drawerFilter) {
      if (searchValue.length >= 2 || searchValue === '') {
        setPage(0);
        const payload = getPayloadData(1);
        getList(payload);
      }
    }
  }, [debouncedValue]);

  useDidMountEffect(() => {
    handleGenerateReport();
  }, [generateReport])

  useEffect(() => {
    return () => {
      resetState();
      context.resetAdmWebhooksReportFilter();
    };
  }, []);

  const handleChangeSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  },[setSearchValue]);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
  }, [setSearchValue]);

  const handleChangeSearchFilter = useCallback((event: SelectChangeEvent) => {
    setSearchFilter(event.target.value);
  }, [setSearchFilter]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, [setPage]);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(+event.target.value);
  },[setRowsPerPage, setPage]);

  const handleDownloadReport = useCallback(() => {
    setShowDownloadModal(true);
  }, [setShowDownloadModal]);

  const handleGenerateReport = (): void => {
    setPage(0);
    setOrder(initialOrderState);
    setSearchValue('')
    const payload = getPayloadData(1);
    getList(payload);

    if (!showMobileTooltip) {
      setShowMobileTooltip(true);
      if (mobile) {
        setGeneralSnackbar({
          open: true,
          type: 'info',
          messageKey: 'common.messages.report_page_loaded'
        });
      }
    }
  };

  const getPayloadData = (page: number) => {
    const orderedColumns = columns.reduce((acc: string[], item) => {
      if (item.checked) {
        acc.push(item.id);
      }
      return acc;
    }, []);

    return {
      page: page,
      per_page: rowsPerPage,
      filters: {
        sent_from: format(startOfDay(new Date(drawerFilter.sent_from)), "yyyy-MM-dd HH:mm:ss"),
        sent_to: format(endOfDay(new Date(drawerFilter.sent_to)), "yyyy-MM-dd HH:mm:ss"),
        affiliateIds: !!drawerFilter.affiliateIds ? getDataIds(drawerFilter.affiliateIds) : [],
        trackerIds: !!drawerFilter.trackerIds ? getDataIds(drawerFilter.trackerIds) : [],
        webhookIds: !!drawerFilter.webhookIds ? getDataIds(drawerFilter.webhookIds) : [],
        webhookTypes: !!drawerFilter.webhookTypes ? getDataIds(drawerFilter.webhookTypes) : [],
        statuses: !!drawerFilter.statuses ? getDataIds(drawerFilter.statuses) : [],
      },
      ...( searchValue.length >= 2 ? {
        search: {
          keyword: searchValue,
        }
      } : {} ),
      ...( !order.hasOwnProperty('none') ? {
        order: { ...order }
      } : {} ),
      encoding: "Unicode, 8-bit",
      ...(hasPermissions(['api2.admin.webhookscontroller.sort_columns']) ? { orderedColumns: orderedColumns } : {}),
    }
  };

  return (
    <>
      {mobile && (<>
        <Box className={headerStyles.header}>
          <Box className={headerStyles.headerBottom}>
            <Box className={headerStyles.headerBottomWrapper}>
              <Typography className={headerStyles.pageTitle} variant="h6">{t("admin.reports.webhooks_report.title")}</Typography>
              <Button
                gradient
                sx={{ width: 'max-content' }}
                disableElevation
                type="submit"
                variant="contained"
                size="small"
                onClick={handleGenerateReport}
              >
                { t("common.buttons.generate") }
              </Button>
            </Box>
          </Box>
        </Box>
      </>)}

      <Box className={styles.filters}>
        <DrawerFilter
          initialFilter={drawerFilter}
          setUpdateFilter={setDrawerFilter}
          order={order}
          setOrder={setOrder}
          onDownload={handleDownloadReport}
          onGenerateReport={handleGenerateReport}
          setGenerateReport={setGenerateReport}
          columns={columns}
          setColumns={setColumns}
          defaultColumns={defaultColumns}
          selectedFilterId={selectedFilterId}
          setSelectedFilterId={setSelectedFilterId}
        >
          {!mobile && (
            <Typography variant="h6">{t("admin.reports.webhooks_report.title")}</Typography>
          )}
        </DrawerFilter>
      </Box>

      <Box className={headerStyles.contentWrapper}>
        <Box className={styles.content}>
          { !!data && (
            <>
              <Box
                className={`${styles.tableHeader} ${!!data ? 'noMargin' : ''} `}
              >
                <CategorySearch
                  hideSearch={false}
                  selected={searchFilter}
                  options={getOptionsList(data.search_fields)}
                  prefix="common.components.report_search_options."
                  value={searchValue}
                  name="search"
                  onChange={handleChangeSearch}
                  onClear={handleClearSearch}
                  onChangeSelect={handleChangeSearchFilter}
                />
              </Box>
              <Box className={styles.table}>
                <TableData
                  list={data.list}
                  columns={data.columns_for_display_data}
                  totalRow={data.sum_columns}
                  order={order}
                  setOrder={setOrder}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalEntries={+data.total_entries}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  actionsCount={1}
                  ActionsComponent={TableActions}
                  translationColumnsPrefix="columns_for_display.table.webhooks_report."
                  stickyHeader={true}
                  detailModalTitle="id"
                  showDetailOnMobile={true}
                  startRowNum={data.start_row_num}
                  endRowNum={data.end_row_num}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <MuiModal
        open={showDownloadModal}
        closeOnOutside={false}
        setToggleModal={setShowDownloadModal}
      >
        {!!filterData && <DownloadReport
          order={order}
          columns={columns}
          downloadData={downloadData}
          encodingTypes={filterData.encoding_types}
          separatorsForFormat={filterData.separators_for_format}
          filter={{
            encoding: "Unicode, 8-bit",
            per_page: rowsPerPage,
            filters: {
              sent_from: drawerFilter.sent_from,
              sent_to: drawerFilter.sent_to,
              affiliateIds: !!drawerFilter.affiliateIds ? getDataIds(drawerFilter.affiliateIds) : [],
              trackerIds: !!drawerFilter.trackerIds ? getDataIds(drawerFilter.trackerIds) : [],
              webhookIds: !!drawerFilter.webhookIds ? getDataIds(drawerFilter.webhookIds) : [],
              webhookTypes: !!drawerFilter.webhookTypes ? getDataIds(drawerFilter.webhookTypes) : [],
              statuses: !!drawerFilter.statuses ? getDataIds(drawerFilter.statuses) : [],
            }
          }}
          setToggleModal={setShowDownloadModal}
          setDownload={setDownload}
          clearDownloadState={clearDownloadState}
          downloadOnThisPage={true}
        />}
      </MuiModal>
    </>
  );
};

export default DesktopReport;
